import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { dispose, DisposableStore } from 'vscode/vscode/vs/base/common/lifecycle';
import { isEqual } from 'vscode/vscode/vs/base/common/resources';
import { IModelService } from 'vscode/vscode/vs/editor/common/services/model';
import { ILanguageService } from 'vscode/vscode/vs/editor/common/languages/language';
import { ITextModelService } from 'vscode/vscode/vs/editor/common/services/resolverService';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { Extensions as Extensions$1 } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { Extensions } from 'vscode/vscode/vs/platform/jsonschemas/common/jsonContributionRegistry';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { IWorkspaceContextService } from 'vscode/vscode/vs/platform/workspace/common/workspace.service';
import { workbenchConfigurationNodeBase } from 'vscode/vscode/vs/workbench/common/configuration';
import { SideBySideEditorInput } from 'vscode/vscode/vs/workbench/common/editor/sideBySideEditorInput';
import { RegisteredEditorPriority } from 'vscode/vscode/vs/workbench/services/editor/common/editorResolverService';
import { IEditorResolverService } from 'vscode/vscode/vs/workbench/services/editor/common/editorResolverService.service';
import { ITextEditorService } from 'vscode/vscode/vs/workbench/services/textfile/common/textEditorService.service';
import { USE_SPLIT_JSON_SETTING, DEFAULT_SETTINGS_EDITOR_SETTING, FOLDER_SETTINGS_PATH } from 'vscode/vscode/vs/workbench/services/preferences/common/preferences';
import { IPreferencesService } from 'vscode/vscode/vs/workbench/services/preferences/common/preferences.service';
import { IUserDataProfileService } from 'vscode/vscode/vs/workbench/services/userDataProfile/common/userDataProfile.service';

const _moduleId = "vs/workbench/contrib/preferences/common/preferencesContribution";
const schemaRegistry = ( (Registry.as(Extensions.JSONContribution)));
let PreferencesContribution = class PreferencesContribution {
    static { this.ID = 'workbench.contrib.preferences'; }
    constructor(modelService, textModelResolverService, preferencesService, languageService, userDataProfileService, workspaceService, configurationService, editorResolverService, textEditorService) {
        this.modelService = modelService;
        this.textModelResolverService = textModelResolverService;
        this.preferencesService = preferencesService;
        this.languageService = languageService;
        this.userDataProfileService = userDataProfileService;
        this.workspaceService = workspaceService;
        this.configurationService = configurationService;
        this.editorResolverService = editorResolverService;
        this.textEditorService = textEditorService;
        this.settingsListener = this.configurationService.onDidChangeConfiguration(e => {
            if (e.affectsConfiguration(USE_SPLIT_JSON_SETTING) || e.affectsConfiguration(DEFAULT_SETTINGS_EDITOR_SETTING)) {
                this.handleSettingsEditorRegistration();
            }
        });
        this.handleSettingsEditorRegistration();
        this.start();
    }
    handleSettingsEditorRegistration() {
        dispose(this.editorOpeningListener);
        if (!!this.configurationService.getValue(USE_SPLIT_JSON_SETTING) || !!this.configurationService.getValue(DEFAULT_SETTINGS_EDITOR_SETTING)) {
            this.editorOpeningListener = this.editorResolverService.registerEditor('**/settings.json', {
                id: SideBySideEditorInput.ID,
                label: ( localizeWithPath(_moduleId, 0, "Split Settings Editor")),
                priority: RegisteredEditorPriority.builtin,
            }, {}, {
                createEditorInput: ({ resource, options }) => {
                    if (isEqual(resource, this.userDataProfileService.currentProfile.settingsResource)) {
                        return { editor: this.preferencesService.createSplitJsonEditorInput(3 , resource), options };
                    }
                    const state = this.workspaceService.getWorkbenchState();
                    if (state === 2 ) {
                        const folders = this.workspaceService.getWorkspace().folders;
                        if (isEqual(resource, folders[0].toResource(FOLDER_SETTINGS_PATH))) {
                            return { editor: this.preferencesService.createSplitJsonEditorInput(5 , resource), options };
                        }
                    }
                    else if (state === 3 ) {
                        const folders = this.workspaceService.getWorkspace().folders;
                        for (const folder of folders) {
                            if (isEqual(resource, folder.toResource(FOLDER_SETTINGS_PATH))) {
                                return { editor: this.preferencesService.createSplitJsonEditorInput(6 , resource), options };
                            }
                        }
                    }
                    return { editor: this.textEditorService.createTextEditor({ resource }), options };
                }
            });
        }
    }
    start() {
        this.textModelResolverService.registerTextModelContentProvider('vscode', {
            provideTextContent: async (uri) => {
                if (uri.scheme !== 'vscode') {
                    return null;
                }
                if (uri.authority === 'schemas') {
                    return this.getSchemaModel(uri);
                }
                return this.preferencesService.resolveModel(uri);
            }
        });
    }
    getSchemaModel(uri) {
        let schema = schemaRegistry.getSchemaContributions().schemas[( (uri.toString()))] ?? {} ;
        const modelContent = JSON.stringify(schema);
        const languageSelection = this.languageService.createById('jsonc');
        const model = this.modelService.createModel(modelContent, languageSelection, uri);
        const disposables = ( (new DisposableStore()));
        disposables.add(schemaRegistry.onDidChangeSchema(schemaUri => {
            if (schemaUri === ( (uri.toString()))) {
                schema = schemaRegistry.getSchemaContributions().schemas[( (uri.toString()))];
                model.setValue(JSON.stringify(schema));
            }
        }));
        disposables.add(model.onWillDispose(() => disposables.dispose()));
        return model;
    }
    dispose() {
        dispose(this.editorOpeningListener);
        dispose(this.settingsListener);
    }
};
PreferencesContribution = ( (__decorate([
    ( (__param(0, IModelService))),
    ( (__param(1, ITextModelService))),
    ( (__param(2, IPreferencesService))),
    ( (__param(3, ILanguageService))),
    ( (__param(4, IUserDataProfileService))),
    ( (__param(5, IWorkspaceContextService))),
    ( (__param(6, IConfigurationService))),
    ( (__param(7, IEditorResolverService))),
    ( (__param(8, ITextEditorService)))
], PreferencesContribution)));
const registry = ( (Registry.as(Extensions$1.Configuration)));
registry.registerConfiguration({
    ...workbenchConfigurationNodeBase,
    'properties': {
        'workbench.settings.enableNaturalLanguageSearch': {
            'type': 'boolean',
            'description': ( localizeWithPath(
                _moduleId,
                1,
                "Controls whether to enable the natural language search mode for settings. The natural language search is provided by a Microsoft online service."
            )),
            'default': true,
            'scope': 3 ,
            'tags': ['usesOnlineServices']
        },
        'workbench.settings.settingsSearchTocBehavior': {
            'type': 'string',
            'enum': ['hide', 'filter'],
            'enumDescriptions': [
                ( localizeWithPath(_moduleId, 2, "Hide the Table of Contents while searching.")),
                ( localizeWithPath(
                    _moduleId,
                    3,
                    "Filter the Table of Contents to just categories that have matching settings. Clicking on a category will filter the results to that category."
                )),
            ],
            'description': ( localizeWithPath(
                _moduleId,
                4,
                "Controls the behavior of the Settings editor Table of Contents while searching. If this setting is being changed in the Settings editor, the setting will take effect after the search query is modified."
            )),
            'default': 'filter',
            'scope': 3
        },
    }
});

export { PreferencesContribution };
